// EditGradeDialog component

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box
} from '@mui/material';
import { Grade, GradeUpdate, Student, Course } from '../types';
import { updateGrade } from '../services/gradeService';

interface EditGradeDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  grade: Grade;
  students: Student[];
  courses: Course[];
}

const EditGradeDialog: React.FC<EditGradeDialogProps> = ({
  open,
  onClose,
  onSuccess,
  grade,
  students,
  courses
}) => {
  const [formData, setFormData] = useState<Partial<GradeUpdate>>({
    assignment_name: '',
    grade: 0,
    max_grade: 100,
    grade_type: 'homework',
    comments: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const gradeTypes = [
    { value: 'exam', label: 'Exam' },
    { value: 'homework', label: 'Homework' },
    { value: 'quiz', label: 'Quiz' },
    { value: 'project', label: 'Project' },
    { value: 'participation', label: 'Participation' },
    { value: 'assignment', label: 'Assignment' }
  ];

  useEffect(() => {
    if (grade) {
      setFormData({
        assignment_name: grade.assignment_name,
        grade: grade.grade,
        max_grade: grade.max_grade,
        grade_type: grade.grade_type,
        comments: grade.comments || ''
      });
    }
  }, [grade]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.assignment_name) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.grade! > formData.max_grade!) {
      setError('Grade cannot be higher than maximum grade');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await updateGrade(grade.id, formData as GradeUpdate);
      onSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to update grade');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof GradeUpdate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Grade</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 300px' }}>
              <TextField
                fullWidth
                label="Student"
                value={students.find(s => s.id === grade.student_id)?.name || ''}
                disabled
              />
            </Box>

            <Box sx={{ flex: '1 1 300px' }}>
              <TextField
                fullWidth
                label="Course"
                value={courses.find(c => c.id === grade.course_id)?.title || ''}
                disabled
              />
            </Box>

            <Box sx={{ flex: '1 1 100%' }}>
              <TextField
                fullWidth
                label="Assignment Name"
                value={formData.assignment_name || ''}
                onChange={(e) => handleChange('assignment_name', e.target.value)}
                required
              />
            </Box>

            <Box sx={{ flex: '1 1 200px' }}>
              <TextField
                fullWidth
                label="Grade"
                type="number"
                value={formData.grade || ''}
                onChange={(e) => handleChange('grade', parseFloat(e.target.value) || 0)}
                required
                inputProps={{ min: 0, step: 0.1 }}
              />
            </Box>

            <Box sx={{ flex: '1 1 200px' }}>
              <TextField
                fullWidth
                label="Maximum Grade"
                type="number"
                value={formData.max_grade || ''}
                onChange={(e) => handleChange('max_grade', parseFloat(e.target.value) || 0)}
                required
                inputProps={{ min: 0, step: 0.1 }}
              />
            </Box>

            <Box sx={{ flex: '1 1 200px' }}>
              <FormControl fullWidth required>
                <InputLabel>Grade Type</InputLabel>
                <Select
                  value={formData.grade_type || ''}
                  label="Grade Type"
                  onChange={(e) => handleChange('grade_type', e.target.value)}
                >
                  {gradeTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: '1 1 100%' }}>
              <TextField
                fullWidth
                label="Comments"
                multiline
                rows={3}
                value={formData.comments || ''}
                onChange={(e) => handleChange('comments', e.target.value)}
              />
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Updating...' : 'Update Grade'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditGradeDialog;
